import { useState } from "react";
import { toast } from "sonner";
import { Download, Upload, FileSpreadsheet, FileText, CheckCircle2, AlertTriangle } from "lucide-react";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const MODULES = [
  { key: "assets", label: "Activos" },
  { key: "incomes", label: "Ingresos" },
  { key: "expenses", label: "Gastos" },
  { key: "insurances", label: "Seguros" },
  { key: "financings", label: "Financiaciones" },
  { key: "commitments", label: "Compromisos" },
];

const IMPORT_FIELDS = {
  assets: [["name", "Nombre", true], ["type", "Tipo", false], ["balance", "Valor actual", true], ["purchase_value", "Valor compra", false], ["acquisition_date", "Fecha adquisición", false], ["notes", "Notas", false]],
  incomes: [["date", "Fecha", true], ["concept", "Concepto", true], ["amount", "Importe", true], ["category", "Familia", false], ["recurring", "Recurrente", false]],
  expenses: [["date", "Fecha", true], ["amount", "Importe", true], ["category", "Familia", false], ["subcategory", "Subfamilia", false], ["comment", "Comentario", false]],
  insurances: [["name", "Nombre", true], ["company", "Compañía", false], ["category", "Tipo", false], ["annual_premium", "Prima anual", true], ["payment_frequency", "Forma de pago", false], ["renewal_date", "Vencimiento", false], ["coverages", "Coberturas", false]],
  financings: [["name", "Nombre", true], ["entity", "Entidad", false], ["original_amount", "Importe original", true], ["start_date", "Fecha inicio", true], ["num_installments", "Nº cuotas", true], ["monthly_payment", "Cuota mensual", true], ["interest_rate", "Interés %", false]],
  commitments: [["name", "Nombre", true], ["type", "Tipo", false], ["category", "Familia", false], ["amount", "Importe", true], ["periodicity", "Periodicidad", false], ["day_of_month", "Día de cobro", false], ["start_date", "Fecha inicio", false], ["end_date", "Fecha fin", false]],
};

async function downloadBlob(url, params, fallback) {
  const r = await api.get(url, { params, responseType: "blob" });
  const cd = r.headers["content-disposition"] || "";
  const fname = /filename=([^;]+)/.exec(cd)?.[1]?.trim() || fallback;
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([r.data]));
  a.download = fname;
  a.click();
  URL.revokeObjectURL(a.href);
}

function ExportSection() {
  const [selected, setSelected] = useState(Object.fromEntries(MODULES.map((m) => [m.key, true])));
  const [format, setFormat] = useState("csv");
  const [busy, setBusy] = useState(false);
  const mods = MODULES.filter((m) => selected[m.key]).map((m) => m.key);

  const doExport = async () => {
    setBusy(true);
    try {
      await downloadBlob("/export", { modules: mods.join(","), format }, `radar-datos.${format === "xlsx" ? "xlsx" : format}`);
      toast.success("Exportación descargada");
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <div className="border border-border rounded-lg p-5 space-y-4" data-testid="export-section">
      <div className="flex items-center gap-2">
        <Download size={18} />
        <h3 className="font-heading font-bold text-lg">Exportar datos</h3>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {MODULES.map((m) => (
          <label key={m.key} className="flex items-center gap-2 border border-border rounded-lg px-3 py-2 cursor-pointer hover:bg-secondary/50">
            <Checkbox data-testid={`export-check-${m.key}`} checked={!!selected[m.key]} onCheckedChange={(v) => setSelected({ ...selected, [m.key]: !!v })} />
            <span className="text-sm">{m.label}</span>
          </label>
        ))}
      </div>
      <div className="flex flex-wrap gap-2" data-testid="export-format-chips">
        {[["csv", "CSV", FileText], ["xlsx", "Excel", FileSpreadsheet], ["pdf", "PDF (informe)", FileText]].map(([k, l, Icon]) => (
          <button
            key={k}
            data-testid={`export-format-${k}`}
            onClick={() => setFormat(k)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${format === k ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-secondary"}`}
          >
            <Icon size={13} /> {l}
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        {format === "pdf" ? "El PDF incluye un resumen con métricas y totales por módulo." : format === "xlsx" ? "Excel con una hoja por módulo y fila de totales." : "CSV con separador «;» (compatible con Excel en español). Varios módulos se descargan en un ZIP."}
      </p>
      <Button data-testid="export-download-button" onClick={doExport} disabled={mods.length === 0 || busy}>
        <Download size={15} className="mr-1" /> {busy ? "Generando…" : `Descargar ${mods.length} módulo${mods.length !== 1 ? "s" : ""}`}
      </Button>
    </div>
  );
}

function ImportSection() {
  const [module, setModule] = useState("expenses");
  const [parsed, setParsed] = useState(null);
  const [mapping, setMapping] = useState({});
  const [validation, setValidation] = useState(null);
  const [busy, setBusy] = useState(false);
  const fields = IMPORT_FIELDS[module];

  const reset = () => { setParsed(null); setMapping({}); setValidation(null); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    reset();
    try {
      const fd = new FormData();
      fd.append("module", module);
      fd.append("file", file);
      const { data } = await api.post("/import/parse", fd);
      setParsed(data);
      setMapping(data.suggested_mapping || {});
      toast.success(`Archivo leído: ${data.total_rows} filas detectadas`);
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
    e.target.value = "";
  };

  const runCommit = async (dryRun) => {
    setBusy(true);
    try {
      const { data } = await api.post("/import/commit", { module, mapping, rows: parsed.rows, dry_run: dryRun });
      setValidation(data);
      if (!dryRun) {
        toast.success(`${data.insertados} registros importados correctamente`);
        reset();
        setValidation({ ...data, finished: true });
      }
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  return (
    <div className="border border-border rounded-lg p-5 space-y-4" data-testid="import-section">
      <div className="flex items-center gap-2">
        <Upload size={18} />
        <h3 className="font-heading font-bold text-lg">Importar datos</h3>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[180px]">
          <label className="text-xs text-muted-foreground">Módulo de destino</label>
          <Select value={module} onValueChange={(v) => { setModule(v); reset(); }}>
            <SelectTrigger data-testid="import-module-select"><SelectValue /></SelectTrigger>
            <SelectContent>{MODULES.map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button data-testid="import-template-csv" variant="outline" size="sm" onClick={() => downloadBlob("/import/template", { module, format: "csv" }, `plantilla-${module}.csv`)}>
          <Download size={13} className="mr-1" /> Plantilla CSV
        </Button>
        <Button data-testid="import-template-xlsx" variant="outline" size="sm" onClick={() => downloadBlob("/import/template", { module, format: "xlsx" }, `plantilla-${module}.xlsx`)}>
          <Download size={13} className="mr-1" /> Plantilla Excel
        </Button>
      </div>

      <div>
        <label className="text-xs text-muted-foreground">Archivo (CSV, Excel; PDF próximamente con extracción inteligente)</label>
        <Input data-testid="import-file-input" type="file" accept=".csv,.txt,.xlsx,.xls,.pdf" onChange={onFile} disabled={busy} className="cursor-pointer" />
      </div>

      {parsed && (
        <div className="space-y-4 border-t border-border pt-4" data-testid="import-mapping-section">
          <div>
            <h4 className="font-heading font-bold text-sm">1 · Mapeo de columnas</h4>
            <p className="text-xs text-muted-foreground">Asocia cada campo con la columna correspondiente de tu archivo.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {fields.map(([field, label, required]) => (
              <div key={field}>
                <label className="text-xs text-muted-foreground">{label}{required && <span className="text-red-600"> *</span>}</label>
                <Select
                  value={mapping[field] != null ? String(mapping[field]) : "skip"}
                  onValueChange={(v) => setMapping({ ...mapping, [field]: v === "skip" ? undefined : parseInt(v) })}
                >
                  <SelectTrigger data-testid={`import-map-${field}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="skip">— No importar —</SelectItem>
                    {parsed.headers.map((h, i) => <SelectItem key={i} value={String(i)}>{h || `Columna ${i + 1}`}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <Button data-testid="import-validate-button" variant="outline" onClick={() => runCommit(true)} disabled={busy}>
            {busy ? "Validando…" : "2 · Validar y previsualizar"}
          </Button>
        </div>
      )}

      {validation && !validation.finished && (
        <div className="space-y-3 border-t border-border pt-4" data-testid="import-validation-section">
          <div className="flex flex-wrap gap-4 text-sm">
            <span className="inline-flex items-center gap-1.5 text-emerald-600 font-semibold" data-testid="import-valid-count"><CheckCircle2 size={15} /> {validation.validos} filas válidas</span>
            <span className={`inline-flex items-center gap-1.5 font-semibold ${validation.errores.length ? "text-red-600" : "text-muted-foreground"}`} data-testid="import-error-count"><AlertTriangle size={15} /> {validation.errores.length} filas con errores</span>
          </div>
          {validation.errores.length > 0 && (
            <div className="border border-red-200 bg-red-50 rounded-lg p-3 max-h-40 overflow-y-auto space-y-1" data-testid="import-errors-list">
              {validation.errores.map((e, i) => (
                <div key={i} className="text-xs text-red-700"><span className="font-bold">Fila {e.fila}:</span> {e.errores.join(" · ")}</div>
              ))}
            </div>
          )}
          {validation.preview.length > 0 && (
            <div className="overflow-x-auto border border-border rounded-lg">
              <table className="w-full text-xs" data-testid="import-preview-table">
                <thead>
                  <tr className="bg-secondary">
                    {fields.map(([f, l]) => <th key={f} className="text-left px-2 py-1.5 font-semibold whitespace-nowrap">{l}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {validation.preview.map((row, i) => (
                    <tr key={i} className="border-t border-border">
                      {fields.map(([f]) => <td key={f} className="px-2 py-1.5 whitespace-nowrap">{row[f] === null || row[f] === undefined ? "—" : String(row[f])}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Button data-testid="import-commit-button" onClick={() => runCommit(false)} disabled={busy || validation.validos === 0}>
            <Upload size={15} className="mr-1" /> 3 · Importar {validation.validos} registros
          </Button>
        </div>
      )}

      {validation?.finished && (
        <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-4 text-sm text-emerald-700 flex items-center gap-2" data-testid="import-success-banner">
          <CheckCircle2 size={16} /> Importación completada: {validation.insertados} registros añadidos{validation.errores.length ? ` · ${validation.errores.length} filas descartadas por errores` : ""}.
        </div>
      )}
    </div>
  );
}

export default function Datos() {
  return (
    <div className="space-y-6" data-testid="datos-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tighter">Exportar / Importar</h1>
        <p className="text-sm text-muted-foreground mt-1">Descarga tus datos en CSV, Excel o PDF, o carga registros de forma masiva con validación previa.</p>
      </div>
      <ExportSection />
      <ImportSection />
    </div>
  );
}
